import i18next from "i18next";
import { loadRemoteTranslationsForLanguage } from "./remote";
import { translations, getSupportedLanguages as getLocalLanguages, } from "./translations";
let i18nInstance = null;
function isPlainObject(value) {
    return typeof value === "object" && value !== null && !Array.isArray(value);
}
function deepMerge(base, override) {
    const result = { ...base };
    for (const [key, overrideValue] of Object.entries(override)) {
        const baseValue = result[key];
        if (isPlainObject(baseValue) && isPlainObject(overrideValue)) {
            result[key] = deepMerge(baseValue, overrideValue);
            continue;
        }
        result[key] = overrideValue;
    }
    return result;
}
/**
 * Initializes the i18n system.
 *
 * @param options - Configuration options
 * @returns Initialized i18n instance
 *
 * @example
 * ```ts
 * await initI18n({ defaultLanguage: 'en', mode: 'auto' });
 *
 * // Later, use translations
 * t('common.continue'); // "Continue"
 * ```
 */
export async function initI18n(options = {}) {
    const { defaultLanguage = "en", fallbackLanguage = "en", debug = false, mode = "local", } = options;
    let resolvedMode;
    if (mode === "auto") {
        // Use local mode only when environment is "local"
        // Use remote mode for dev/test/uat/prod environments
        // Check MODE first (Vite's build mode)
        const viteMode = import.meta.env?.MODE;
        // Check VITE_ENV if available (custom environment variable)
        const viteEnv = import.meta.env?.VITE_ENV;
        // Use local mode when environment is "local" or "i18nlocal" (Vite reserves "local" for .env.*.local)
        // Otherwise use remote (for dev/test/uat/prod environments)
        if (viteMode === "local" || viteEnv === "local" || viteMode === "i18nlocal" || viteEnv === "i18nlocal") {
            resolvedMode = "local";
        }
        else {
            resolvedMode = "remote";
        }
    }
    else {
        resolvedMode = mode;
    }
    const resources = {};
    if (resolvedMode === "remote") {
        // Remote mode: load CDN-backed translations for all supported languages
        // to enable language switching via changeLanguage().
        const supportedLanguages = getLocalLanguages();
        const translationPromises = supportedLanguages.map(async (lang) => {
            const remoteTranslations = await loadRemoteTranslationsForLanguage(lang);
            return { lang, translations: remoteTranslations };
        });
        const loadedTranslations = await Promise.all(translationPromises);
        for (const { lang, translations: langTranslations } of loadedTranslations) {
            const bundledTranslations = translations[lang] ?? {};
            resources[lang] = {
                translation: deepMerge(bundledTranslations, langTranslations),
            };
        }
    }
    else {
        // Local mode: build resources from bundled JSON translations.
        for (const [lang, bundle] of Object.entries(translations)) {
            resources[lang] = {
                translation: bundle,
            };
        }
    }
    await i18next.init({
        resources,
        lng: defaultLanguage,
        fallbackLng: fallbackLanguage,
        debug,
        interpolation: {
            escapeValue: false, // React already escapes
        },
        returnNull: false,
        returnEmptyString: false,
    });
    i18nInstance = i18next;
    return i18next;
}
/**
 * Gets the current i18n instance.
 * Throws if not initialized.
 */
function getInstance() {
    if (!i18nInstance) {
        throw new Error("i18n not initialized. Call initI18n() first.");
    }
    return i18nInstance;
}
/**
 * Translates a key to the current language.
 *
 * @param key - Translation key (e.g., 'common.continue')
 * @param options - Interpolation options
 * @returns Translated string
 *
 * @example
 * ```ts
 * t('common.continue'); // "Continue"
 * t('errors.required'); // "This field is required"
 * ```
 */
export function t(key, options) {
    return getInstance().t(key, options);
}
/**
 * Changes the current language.
 *
 * @param lang - Language code (e.g., 'en')
 *
 * @example
 * ```ts
 * await changeLanguage('en');
 * t('common.continue'); // "Continue"
 * ```
 */
export async function changeLanguage(lang) {
    await getInstance().changeLanguage(lang);
}
/**
 * Gets the current language code.
 *
 * @returns Current language code
 */
export function getCurrentLanguage() {
    return getInstance().language;
}
/**
 * Gets all supported language codes.
 *
 * @returns Array of supported language codes
 */
export function getSupportedLanguages() {
    return getLocalLanguages();
}
/**
 * Gets the raw i18next instance for advanced usage.
 *
 * @returns i18next instance
 */
export function getI18nInstance() {
    return getInstance();
}
//# sourceMappingURL=i18n.js.map